import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { OrderPaymentProofUrlResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseSellerOrderProofQueryParams = {
  storeSlug: string;
  orderId: string;
  /** Skip the request when the caller knows the order carries no proofs —
   *  the endpoint 404s in that case. Defaults to enabled. */
  enabled?: boolean;
};

export function useOrderProofQuery({
  storeSlug,
  orderId,
  enabled = true,
}: UseSellerOrderProofQueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.orderProof(storeSlug, orderId),
    queryFn: () =>
      apiJsonClient<OrderPaymentProofUrlResponse>(endpoints.seller.orderProof(orderId)),
    enabled: enabled && storeSlug.length > 0 && orderId.length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Signed proof URLs keyed by proof id, for views that render the proof list off
 * the order detail payload (which carries only object keys). Payment proofs live
 * in the private bucket, so a URL can only come from the backend — never build
 * one from `objectKey` against the public media zone.
 */
export function useOrderProofUrlsById({
  storeSlug,
  orderId,
  enabled = true,
}: UseSellerOrderProofQueryParams): Map<string, string> {
  const { data } = useOrderProofQuery({ storeSlug, orderId, enabled });
  return useMemo(() => {
    const entries = data?.items ?? [];
    return new Map(entries.map((item) => [item.proofId, item.url]));
  }, [data]);
}
